import { getFragmentData } from 'gql'
import { AuthUserFragmentFragmentDoc, ErrorFragmentFragmentDoc } from 'gql/graphql'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { authUserFromDTO } from 'features/user/api/authUser'
import { useAbility } from 'providers/casl'
import { mapGraphQLErrors } from 'providers/tanstack-query/errors'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { useAuthenticate } from '../useAuthenticate'
import { SIGN_IN } from './api/mutation'

import type { UseSignIn } from './types'
import type { Schema } from 'features/user/forms/UserLoginForm/schema'
import type { SignInMutation, SignInMutationVariables } from 'gql/graphql'
import type { FieldErrors } from 'react-hook-form'

export const useSignIn = (): UseSignIn => {
  const { t } = useTranslation('features/user')
  const { authenticate } = useAuthenticate()
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})
  const ability = useAbility()

  const { isPending, mutateAsync } = useMutation<SignInMutationVariables, SignInMutation>({
    mutation: SIGN_IN,
    onCompleted: async ({ signIn }: SignInMutation) => {
      const accessToken = signIn?.accessToken
      const refreshToken = signIn?.refreshToken
      const errors = getFragmentData(ErrorFragmentFragmentDoc, signIn?.errors)
      const user = getFragmentData(AuthUserFragmentFragmentDoc, signIn?.user)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
      } else if (accessToken && refreshToken && user) {
        setErrors({})
        authenticate({ accessToken, refreshToken, user: authUserFromDTO(user) })
        enqueueSnackbar(t('login.success'), { variant: 'success' })
      }
    },
  })

  const signIn = (variables: Schema): void => {
    void mutateAsync({
      ...variables,
      canAccessGamConnection: ability.can('access', 'GlobalSetupPage-googleAdManagerConnection'),
    })
  }

  return {
    errors,
    loading: isPending,
    signIn,
  }
}
